import type { Padding } from './types';
import { useRef, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { updateViewState, updateMapState } from '@/features/analysisMapSlice';
import { useWindowDimensions } from '@/hooks/useWindowDimension';
import isEqualWith from 'lodash/isEqualWith';
import { determinePadding } from '@/views/map/ItemSelection';
import {
  DESKTOP_VIEWBOX_PADDING,
  MOBILE_VIEWBOX_PADDING,
  updateViewport,
} from '@/views/map/deckglUtils';

/**
 * Handles viewport updates based on the current view state and bounding box of items.
 * It does some basic checks to ensure the viewport is updated only when necessary,
 * and uses padding to ensure items are fully visible within the viewport.
 *
 * @returns A function to update the viewport based on the current view state and bounding box of items.
 */
export const useUpdateViewport = () => {
  const dispatch = useAppDispatch();
  const { width, height } = useWindowDimensions();
  const currentViewState = useAppSelector(
    (state) => state.analysisMap.viewState
  );

  // Use a ref to store the latest viewState to avoid dependency issues
  const viewStateRef = useRef(currentViewState);

  useEffect(() => {
    viewStateRef.current = currentViewState;
  }, [currentViewState]);

  return useCallback(
    /**
     *
     * @param bbox Bounding box of items to fit
     * @param utilizeItemPadding Whether to use item padding for the viewport calculation (zooms out a bit more to fit the popup)
     */
    (bbox: number[] | null | undefined, utilizeItemPadding = false) => {
      // Use the ref to get the latest state without it being a dependency
      const latestViewState = viewStateRef.current;
      let itemPadding: Required<Padding> | undefined;

      if (utilizeItemPadding && bbox) {
        itemPadding = determinePadding(
          width,
          height,
          false, // Assuming layer manager is not open, need to fix this at some point
          bbox,
          viewStateRef.current.zoom
        );
      }

      const updatedViewport = updateViewport(
        latestViewState,
        bbox,
        width,
        itemPadding ?? DESKTOP_VIEWBOX_PADDING,
        itemPadding ?? MOBILE_VIEWBOX_PADDING
      );

      // Only dispatch if the viewport actually changed meaningfully
      if (
        updatedViewport &&
        !isEqualWith(updatedViewport, latestViewState, (x, y) => {
          // We aren't comparing functions, because lodash compares functions by reference
          if (typeof x === 'function' && typeof y === 'function') {
            return true;
          }

          // For numbers, use a small epsilon to avoid unnecessary updates
          if (typeof x === 'number' && typeof y === 'number') {
            return Math.abs(x - y) < 0.0001;
          }
          return undefined;
        })
      ) {
        dispatch(updateViewState(updatedViewport));

        // Also update mapState so coordinates persist across navigation
        dispatch(updateMapState(updatedViewport));
      }
    },
    [dispatch, height, width]
  );
};
